// ==========================================================================
// React Query Hooks for Actions Data
// Includes polling logic for evidence approval
// ==========================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActions,
  getActionDetail,
  activateAction,
  completeStep,
  uploadEvidence,
} from '@/services/action.service';
import type { UserActionDetail } from '@/types/action.types';

// Query keys
export const actionKeys = {
  all: ['actions'] as const,
  lists: () => [...actionKeys.all, 'list'] as const,
  list: (status?: string) => [...actionKeys.lists(), { status }] as const,
  details: () => [...actionKeys.all, 'detail'] as const,
  detail: (id: number) => [...actionKeys.details(), id] as const,
};

/**
 * Hook to fetch all user actions
 * @param status - Optional filter: 'active', 'assigned', 'completed', 'expired'
 */
export function useActions(status?: string, enabled = true) {
  return useQuery({
    queryKey: actionKeys.list(status),
    queryFn: () => getActions(status),
    enabled: enabled && !!localStorage.getItem('access_token'),
    staleTime: 2 * 60 * 1000, // Data fresh for 2 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to fetch action detail with steps
 * @param actionId - Action ID (null to disable)
 */
export function useActionDetail(actionId: number | null) {
  return useQuery({
    queryKey: actionKeys.detail(actionId!),
    queryFn: () => getActionDetail(actionId!),
    enabled: !!actionId && !!localStorage.getItem('access_token'),
    staleTime: 60 * 1000, // Data fresh for 60 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to activate an action (assigned -> active)
 */
export function useActivateAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (actionId: number) => activateAction(actionId),
    onSuccess: async () => {
      // Invalidate all action caches to refetch and ensure UI is updated with fresh data
      await queryClient.invalidateQueries({ queryKey: actionKeys.all });
    },
  });
}

/**
 * Hook to complete a step
 */
export function useCompleteStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, stepId }: { actionId: number; stepId: number }) =>
      completeStep(actionId, stepId),
    onSuccess: (updatedStep, { actionId }) => {
      // Update the step in the detail cache immediately
      queryClient.setQueryData<UserActionDetail>(actionKeys.detail(actionId), (old) => {
        if (!old) return old;
        const newSteps = old.steps.map((s) => (s.id === updatedStep.id ? updatedStep : s));
        const completedCount = newSteps.filter((s) => s.status === 'completed').length;
        return {
          ...old,
          steps: newSteps,
          progress: {
            ...old.progress,
            completed_steps: completedCount,
          },
        };
      });
      // Refetch to pick up any backend-side changes (e.g. action auto-completion)
      queryClient.invalidateQueries({ queryKey: actionKeys.detail(actionId) });
      queryClient.invalidateQueries({ queryKey: actionKeys.lists() });
    },
  });
}

/**
 * Hook to upload evidence for a step
 */
export function useUploadEvidence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ actionId, stepId, file }: { actionId: number; stepId: number; file: File }) =>
      uploadEvidence(actionId, stepId, file),
    onSuccess: (updatedStep, { actionId }) => {
      // Update the step in the detail cache immediately
      queryClient.setQueryData<UserActionDetail>(actionKeys.detail(actionId), (old) => {
        if (!old) return old;
        return {
          ...old,
          steps: old.steps.map((s) => (s.id === updatedStep.id ? updatedStep : s)),
        };
      });
      // Refetch to pick up any backend-side status changes (e.g. auto-approval)
      queryClient.invalidateQueries({ queryKey: actionKeys.detail(actionId) });
    },
  });
}

/**
 * Hook to invalidate all action caches
 */
export function useInvalidateActions() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: actionKeys.all });
}

/**
 * Hook to clear all action caches
 */
export function useClearActionsCache() {
  const queryClient = useQueryClient();
  return () => queryClient.removeQueries({ queryKey: actionKeys.all });
}
